// Tasks dependencies helpers split from the legacy mega-file.
import { sql, sqlRaw } from "../../base";
import type { Task } from "../../types";

export const dependenciesTasks = {
  async updateDependencies(taskId: string, dependsOn: string[], userId: string): Promise<Task> {
    try {
      // Ensure the caller owns the task
      const current = await sql`
        SELECT * FROM tasks WHERE id = ${taskId} AND (user_id = ${userId} OR assigned_to = ${userId})
      `;
      if (!current || current.length === 0) throw new Error('Task not found or not owned');

      // Reset existing edges
      await sqlRaw('DELETE FROM task_dependencies WHERE task_id = $1', [taskId]);
      // Insert new edges
      if (dependsOn && dependsOn.length > 0) {
        const values: any[] = [];
        const tuples = dependsOn.map((depId, idx) => {
          values.push(taskId, depId);
          const a = idx * 2 + 1;
          const b = idx * 2 + 2;
          return `($${a}, $${b})`;
        }).join(', ');
        await sqlRaw(`INSERT INTO task_dependencies (task_id, depends_on_task_id) VALUES ${tuples}`, values);
      }

      // Recompute is_live: live when no deps OR all deps complete
      const recompute = await sqlRaw(
        `UPDATE tasks AS t
         SET is_live = (
           CASE WHEN NOT EXISTS (SELECT 1 FROM task_dependencies td WHERE td.task_id = t.id)
                THEN 1
                ELSE (
                  CASE WHEN NOT EXISTS (
                           SELECT 1 FROM task_dependencies td
                           JOIN tasks d ON d.id = td.depends_on_task_id
                           WHERE td.task_id = t.id AND (d.status IS NULL OR d.status != 'Complete')
                       ) THEN 1 ELSE 0 END
                )
           END
         ), updated_at = CURRENT_TIMESTAMP
         WHERE t.id = $1 AND (t.user_id = $2 OR t.assigned_to = $2)
         RETURNING *`,
        [taskId, userId]
      );
      return recompute[0] as Task;
    } catch (error) {
      console.error('Error updating dependencies:', error);
      throw error;
    }
  },

  async calculateLiveTasks(goalId: string): Promise<void> {
    try {
      // Recompute is_live for all tasks under a goal
      await sqlRaw(
        `UPDATE tasks AS t
         SET is_live = (
           CASE WHEN NOT EXISTS (SELECT 1 FROM task_dependencies td WHERE td.task_id = t.id)
                THEN 1
                ELSE (
                  CASE WHEN NOT EXISTS (
                           SELECT 1 FROM task_dependencies td
                           JOIN tasks d ON d.id = td.depends_on_task_id
                           WHERE td.task_id = t.id AND (d.status IS NULL OR d.status != 'Complete')
                       ) THEN 1 ELSE 0 END
                )
           END
         ), updated_at = CURRENT_TIMESTAMP
         WHERE t.goal_id = $1`,
        [goalId]
      );
    } catch (error) {
      console.error('Error calculating live tasks:', error);
    }
  },

  async getDependencyChain(taskId: string): Promise<any[]> {
    try {
      // Use a recursive query supported by SQLite
      const result = await sqlRaw(
        `WITH RECURSIVE chain(task_id, depends_on_task_id, depth) AS (
           SELECT td.task_id, td.depends_on_task_id, 1 FROM task_dependencies td WHERE td.task_id = $1
           UNION ALL
           SELECT td.task_id, td.depends_on_task_id, c.depth + 1
           FROM task_dependencies td
           JOIN chain c ON td.task_id = c.depends_on_task_id
         )
         SELECT c.*, t.title as task_title, d.title as depends_on_title
         FROM chain c
         LEFT JOIN tasks t ON t.id = c.task_id
         LEFT JOIN tasks d ON d.id = c.depends_on_task_id
         ORDER BY depth ASC`,
        [taskId]
      );
      return result || [];
    } catch (error) {
      console.error('Error getting dependency chain:', error);
      return [];
    }
  },

  async getLiveTasks(goalId: string, userId: string): Promise<Task[]> {
      try {
        const result = await sql`
          SELECT * FROM tasks
          WHERE goal_id = ${goalId}
          AND (user_id = ${userId} OR assigned_to = ${userId})
          AND is_live = 1
          AND status != 'Complete'
          ORDER BY 
            CASE priority
              WHEN 'Critical' THEN 1
              WHEN 'High' THEN 2
              WHEN 'Medium' THEN 3
              WHEN 'Low' THEN 4
              ELSE 5
            END ASC,
            position ASC
        `;
        return (result || []) as Task[];
      } catch (error) {
        console.error('Error fetching live tasks:', error);
        return [];
      }
    },

  async getBlockedTasks(goalId: string, userId: string): Promise<Task[]> {
      try {
        const result = await sql`
          SELECT t.*, 
                 GROUP_CONCAT(bt.title, ', ') as blocking_task_titles
          FROM tasks t
          JOIN task_dependencies td ON td.task_id = t.id
          JOIN tasks bt ON bt.id = td.depends_on_task_id AND bt.status != 'Complete'
          WHERE t.goal_id = ${goalId}
          AND (t.user_id = ${userId} OR t.assigned_to = ${userId})
          AND t.is_live = 0
          AND t.status != 'Complete'
          GROUP BY t.id
          ORDER BY t.position ASC
        `;
        return (result || []) as Task[];
      } catch (error) {
        console.error('Error fetching blocked tasks:', error);
        return [];
      }
    },
};
